import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api, { getCsrfToken, setCsrfToken } from '../services/api';

const AuthContext = createContext();

const CSRF_CACHE_KEY = 'csrfToken';
const CSRF_CACHE_TS = 'csrfTokenAt';
const CSRF_MAX_AGE_MS = 30 * 60 * 1000;
const CSRF_MIN_INTERVAL_MS = 5000;

const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const csrfFetchRef = useRef(null);

  const loadCachedCsrf = useCallback(() => {
    try {
      const token = safeGetItem(CSRF_CACHE_KEY);
      const ts = Number(safeGetItem(CSRF_CACHE_TS) || 0);
      if (token && Date.now() - ts < CSRF_MAX_AGE_MS) {
        setCsrfToken(token);
        return token;
      }
    } catch (err) {
      // ignore storage issues
    }
    return null;
  }, []);

  const saveCachedCsrf = (token) => {
    try {
      localStorage.setItem(CSRF_CACHE_KEY, token);
      localStorage.setItem(CSRF_CACHE_TS, String(Date.now()));
    } catch (err) {
      // ignore storage issues
    }
  };

  const fetchCsrfToken = useCallback(async () => {
    if (csrfFetchRef.current) return csrfFetchRef.current;
    const now = Date.now();
    const lastFetch = Number(safeGetItem(CSRF_CACHE_TS) || 0);
    if (now - lastFetch < CSRF_MIN_INTERVAL_MS) {
      return getCsrfToken() || loadCachedCsrf();
    }
    csrfFetchRef.current = api.get('/auth/csrf')
      .then((csrfResponse) => {
        const token = csrfResponse.data?.token;
        if (token) {
          setCsrfToken(token);
          saveCachedCsrf(token);
        }
        return token;
      })
      .catch(() => getCsrfToken() || loadCachedCsrf())
      .finally(() => {
        csrfFetchRef.current = null;
      });
    return csrfFetchRef.current;
  }, [loadCachedCsrf]);

  useEffect(() => {
    let isMounted = true;
    const loadUser = async () => {
      if (window.location?.pathname === '/login') {
        if (isMounted) setLoading(false);
        return;
      }
      try {
        loadCachedCsrf();
        await fetchCsrfToken();
        const response = await api.get('/auth/me');
        if (isMounted) {
          setUser(response.data?.data || null);
        }
      } catch (err) {
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadUser();
    return () => {
      isMounted = false;
    };
  }, [fetchCsrfToken, loadCachedCsrf]);

  const login = async (email, password, mfaToken = '', backupCode = '') => {
    try {
      setError(null);
      if (!getCsrfToken()) {
        loadCachedCsrf();
        await fetchCsrfToken();
      }
      const response = await api.post('/auth/login', { email, password, mfaToken, backupCode });
      
      const { data } = response.data;
      setUser(data);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed';
      const mfaRequired = !!err.response?.data?.mfaRequired;
      const mfaSetupRequired = !!err.response?.data?.mfaSetupRequired;
      const setupToken = err.response?.data?.setupToken || null;
      setError(message);
      return { success: false, message, mfaRequired, mfaSetupRequired, setupToken };
    }
  };

  const logout = () => {
    api.post('/auth/logout').catch(() => {});
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
